/**
 * FleetEditModal — edit the rental settings of one fleet car (per-section modal). Enrolment is the
 * full-page `FleetEnrollForm`; both share `FleetFormBody`. PUTs pobočka / tarif / kategorie / note. It is also
 * the one home of "Vyřadit z půjčovny" (confirmed, bottom left, away from Uložit): taking a car out is rare
 * and must not sit one click away on every row of the list.
 */

import { Button } from '@/components/Button';
import { useConfirm } from '@/components/ConfirmDialog';
import { Modal, DialogCrown } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import {
  useKategorieNabidkaQuery,
  usePobockyQuery,
  useRemoveFleetMutation,
  useTarifyQuery,
  useUpdateFleetMutation,
  type FleetEntry,
} from './api';
import { FLEET_FORM_ID, FleetFormBody, fleetSeed, fleetToBody, withCurrent } from './fleet-section-forms';

interface FleetEditModalProps {
  entry: FleetEntry;
  /** What the dialog titles the car with (plate or model). */
  title: string;
  /** `pujcovna:admin`: may edit the car's pricing terms. */
  canAdmin?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FleetEditModal({ entry, title, canAdmin = false, open, onOpenChange }: FleetEditModalProps) {
  const update = useUpdateFleetMutation(entry.id);
  const remove = useRemoveFleetMutation();
  const { confirm, dialog } = useConfirm();
  const busy = update.isPending || remove.isPending;
  // Every way out ends in `onOpenChange(false)`; while a save or a take-out is pending it would unmount the
  // observer the success / error toasts hang on, so it waits.
  const guarded = (next: boolean) => {
    if (!next && busy) return;
    onOpenChange(next);
  };

  const takeOut = async () => {
    const ok = await confirm({
      title: (
        <>
          Vyřadit <em>{title}</em> z půjčovny?
        </>
      ),
      description: 'Vůz zůstane ve skladu, jen se přestane nabízet v půjčovně. Kdykoli ho můžete zařadit znovu.',
      confirmLabel: 'Vyřadit',
    });
    if (!ok) return;
    remove.mutate(entry.id, {
      onSuccess: () => {
        toast.ok('Vozidlo vyřazeno z půjčovny.');
        onOpenChange(false);
      },
      onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se vyřadit vozidlo.'}`),
    });
  };
  const pobocky = usePobockyQuery();
  const tarify = useTarifyQuery();
  const kategorie = useKategorieNabidkaQuery();
  // The selects are controlled by the entry's ids: rendered before their options exist (or after a failed
  // request) they would show "Bez …" while the form still holds the old id, and a save would keep it
  // unseen. So the body waits for all three lookups, and a failure blocks the save.
  const lookups = [pobocky, tarify, kategorie];
  const loading = lookups.some((q) => q.isLoading);
  const failed = lookups.some((q) => q.isError);

  return (
    <>
      <Modal open={open} onOpenChange={guarded} maxWidth={720}>
        <DialogCrown
          eyebrow="Vozidlo půjčovny"
          title={
            <>
              Upravit <em>{title}</em>
            </>
          }
          sub="Pobočka, tarif, kategorie a cenové podmínky vozu v půjčovně. Údaje o voze se mění ve skladu."
        />
        <Modal.Body className="px-8 py-5">
          {failed ? (
            <p data-testid="fleet-lookups-error" className="text-[13px] text-ink-2">
              Pobočky, tarify nebo kategorie se nepodařilo načíst, vozidlo teď nelze upravit. Zkuste to prosím znovu za chvíli.
            </p>
          ) : loading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <FleetFormBody
              initial={fleetSeed(entry)}
              entry={entry}
              canAdmin={canAdmin}
              busy={busy}
              pobocky={withCurrent(pobocky.data ?? [], entry.id_pobocky, entry.pobocka_nazev)}
              tarify={withCurrent(tarify.data ?? [], entry.id_tarifu, entry.tarif_nazev)}
              kategorie={withCurrent(kategorie.data ?? [], entry.id_kategorie, entry.kategorie_nazev)}
              onSubmit={(form) => {
                if (!form.id_pobocky) {
                  toast.error('Vyberte pobočku.');
                  return;
                }
                const body = fleetToBody(form, entry);
                if (!body.ok) {
                  toast.error(body.chyba);
                  return;
                }
                update.mutate(body.body, {
                  onSuccess: () => {
                    toast.ok('Vozidlo uloženo.');
                    onOpenChange(false);
                  },
                  onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se uložit vozidlo.'}`),
                });
              }}
            />
          )}
        </Modal.Body>
        <Modal.Foot
          meta={
            <Button variant="secondary" onClick={takeOut} disabled={busy}>
              Vyřadit z půjčovny
            </Button>
          }
          actions={
            <>
              <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
                Zrušit
              </Button>
              <Button variant="ink" type="submit" form={FLEET_FORM_ID} disabled={busy || loading || failed}>
                Uložit
              </Button>
            </>
          }
        />
      </Modal>
      {dialog}
    </>
  );
}
